/**
 * The twenty-one source-system connectors, ported from `OEM+_Platform_Demo.html`.
 *
 * Source lines
 * ------------
 * - `CONN` — demo lines 2456–2478 (21 connectors), consumed at demo lines
 *   2479–2480 (the connector cards) and demo line 3016 (`sysRow` inside
 *   `renderModule`, which looks a connector up by `c[0]` and reads `c[1]`,
 *   `c[5]` and `c[6]`).
 *
 * What changed in the port
 * ------------------------
 * 1. **The positional tuple became a named-field interface.** The demo stored
 *    each connector as a seven-element array. Field names below are taken from
 *    how the two consumers index it:
 *
 *    | index | field         | demo usage                                        |
 *    |-------|---------------|---------------------------------------------------|
 *    | `[0]` | `name`        | card `<h3>`, and the key `MODULES[].systems` joins on |
 *    | `[1]` | `subtitle`    | the dimmed span after the name                     |
 *    | `[2]` | `description` | `<div class="desc">`                               |
 *    | `[3]` | `stats`       | `<div class="stats">`, one `<span>` per entry      |
 *    | `[4]` | `tone`        | pill CSS class                                     |
 *    | `[5]` | `status`      | pill label; `'Live'` also drives the count and the module-page badge |
 *    | `[6]` | `icon`        | inline SVG children for a `0 0 24 24` viewBox      |
 *
 * 2. **Stats keep their interpolations.** Each stat was a template literal over
 *    the loaded datasets. It is carried as a {@link LiveFigure}: literal text
 *    verbatim in `{ text }` segments, the demo's expression verbatim in
 *    `{ expr }` segments. Nothing is evaluated and no number is baked in.
 *
 * 3. **Markup stripped.** The card wrapper, `<svg>` element, pill `<span>` and
 *    the `s.replace(/^([\d,]+)/,'<b>$1</b>')` that bolded a stat's leading
 *    number all lived in the render call, not in the data — so only the raw
 *    SVG path children survive, in `icon`. The leading-number emphasis is
 *    recoverable from the segment structure: a stat that opens with an `expr`
 *    segment is exactly the case the demo bolded.
 *
 * 4. **No handler strings.** `CONN` carried none; the card had no inline
 *    handler at all. The `show('connectors')` handler `renderModule` puts on
 *    the system row belongs to the scene, not the seed.
 *
 * Data Schema layer — pure data and pure types. No React, no side effects.
 */

import { ft, fx, type LiveFigure } from './modules';

/** Pill tone the demo applied to a connector's status. */
export type ConnectorTone = 'good' | 'warn';

/** Status labels the demo used. */
export const CONNECTOR_STATUSES = ['Connected', 'Live', 'Shift-end', 'Partial'] as const;

/** Closed set of connector status labels. */
export type ConnectorStatus = (typeof CONNECTOR_STATUSES)[number];

/** One source system feeding the platform. */
export interface OemConnector {
  /** Software package name — the key `OemModule.systems` joins on. */
  readonly name: string;
  /** The dimmed qualifier printed after the name. */
  readonly subtitle: string;
  /** What is imported and how often. */
  readonly description: string;
  /** Stat chips under the description, each as a live-figure template. */
  readonly stats: readonly LiveFigure[];
  /** Pill tone. */
  readonly tone: ConnectorTone;
  /** Pill label. */
  readonly status: ConnectorStatus;
  /** Inline SVG children for a `0 0 24 24` viewBox. */
  readonly icon: string;
}

/** Demo lines 2456–2478 — `const CONN=[…]`, 21 connectors in demo order. */
export const CONNECTORS: readonly OemConnector[] = [
  {
    name: 'IBM Maximo · service register',
    subtitle: 'Meridian\'s own EAM — every machine it services (Maximo Application Suite)',
    description:
      'The aftermarket system of record: an asset record per serial number with meters, components, PM schedule, work-order history, failure codes, service contract and document links; MXWO per site, MXPM, MXMETER. Nightly MIF/OSLC plus on-change.',
    stats: [
      [fx('Object.keys(B.mx).length'), ft(' assets')],
      [fx('B.mx_contracts.length'), ft(' contracts')],
      [ft('MIF · OSLC · CSV · XLSX')],
    ],
    tone: 'good',
    status: 'Connected',
    icon: '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 9h10M7 13h10M7 17h6"/>',
  },
  {
    name: 'SAP PM',
    subtitle: 'Customer CMMS (Kalbarri, Broken Ridge, Mount Isolde, Ochre Hill)',
    description:
      'Work orders, PM schedule, component register, cost postings at four customer sites. Nightly IDoc/JSON export; versioned with diff; cross-referenced to the Maximo service register.',
    stats: [
      [fx("WOS.filter(w=>w.mine!=='GD').length.toLocaleString()"), ft(' WOs')],
      [ft('4 plants')],
      [ft('last import 02:10')],
    ],
    tone: 'good',
    status: 'Connected',
    icon: '<path d="M9 4h6l1 3h3v13H5V7h3z"/><path d="M9 12l2 2 4-4"/>',
  },
  {
    name: 'IBM Maximo · Gawler Deep EAM',
    subtitle: 'Customer system — Maximo to Maximo',
    description:
      'Gawler Deep runs its own Maximo: MXWO and MXASSET object structures through the integration framework (XML) and OSLC REST (JSON); Start Center KPIs as Excel. Cross-referenced to the service register by external reference id.',
    stats: [
      [fx("WOS.filter(w=>w.mine==='GD').length.toLocaleString()"), ft(' WOs')],
      [fx('B.maximo_assets.length'), ft(' assets')],
      [ft('MIF · OSLC · XLSX')],
    ],
    tone: 'good',
    status: 'Connected',
    icon: '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 9h10M7 13h10M7 17h6"/>',
  },
  {
    name: 'Pitram',
    subtitle: 'Fleet management system (Kalbarri, Broken Ridge, Mount Read)',
    description:
      'Shift-end consolidation of onboard logs: hours, production, alarms counts, abuse events, delay codes. XML export at 06:05 / 18:05 — trends a shift late.',
    stats: [
      [fx("FLEET.filter(f=>['KG','BR','MR'].includes(f.mine)).length"), ft(' machines')],
      [ft('2 exports/day')],
      [ft('last 06:05')],
    ],
    tone: 'good',
    status: 'Shift-end',
    icon: '<path d="M4 18l4-6 4 3 4-8 4 5"/>',
  },
  {
    name: 'Underground Wi-Fi / LTE telematics',
    subtitle: 'Live machine data (Mount Isolde, Gawler Deep)',
    description:
      'MQTT broker on the site network; 10 s payloads per machine normalised by the live collector. CAN bus captures on demand.',
    stats: [
      [
        fx("FLEET.filter(f=>['MI','GD'].includes(f.mine)&&f.cls!=='sdrill').length"),
        ft(' machines'),
      ],
      [ft('10 s cadence')],
      [ft('LIVE')],
    ],
    tone: 'good',
    status: 'Live',
    icon: '<path d="M12 20v-6M8 12a6 6 0 018 0M5 8a10 10 0 0114 0"/>',
  },
  {
    name: 'RMS',
    subtitle:
      'Remote Monitoring Service — Meridian Reliability Centre (Mount Isolde, Ochre Hill, Gawler Deep)',
    description:
      'Specialists and algorithms over the connected fleet: per-machine health index by system, operator-behaviour scorecards, instant alerts with a recommendation, a weekly review pack and a monthly fleet-health report per site. On the surface drills the feed also carries per-hole penetration, air, torque and make-up/breakout torque per joint.',
    stats: [
      [fx('Object.keys(B.rms_health).length'), ft(' machines')],
      [fx('B.rms_alerts.length'), ft(' alerts (Aug–Sep)')],
      [ft('JSON · CSV · JSONL · PDF')],
    ],
    tone: 'good',
    status: 'Live',
    icon: '<circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M2 12h4M18 12h4"/>',
  },
  {
    name: 'Salesforce',
    subtitle: 'Parts portal & service cases',
    description:
      'Catalogue, stock by site store and DC, orders, warranty and technical cases. Agents draft requisitions here.',
    stats: [
      [fx('B.parts.length'), ft(' parts')],
      [ft('10 locations')],
      [fx('B.cases.filter(c=>/Open/.test(c.status)).length'), ft(' open cases')],
    ],
    tone: 'good',
    status: 'Connected',
    icon: '<path d="M4 7h16l-1.5 9h-13z"/><path d="M9 20h.01M15 20h.01"/>',
  },
  {
    name: 'CatBase',
    subtitle: 'Digital parts books & catalogue database',
    description:
      'Illustrated parts catalogue per model: exploded-view sections with item-numbered bills of materials (PDF), the catalogue export (XML), the BOM behind every callout (CSV) and serial-range applicability — what the assistant reads to resolve "item 3 on section 09" to a part number.',
    stats: [
      [
        fx("B.extra.filter(e=>e.folder==='14_catbase'&&e.kind==='pdf').length"),
        ft(' book sections'),
      ],
      [fx('B.catbase_sections.length'), ft(' callouts')],
      [ft('PDF · XML · CSV')],
    ],
    tone: 'good',
    status: 'Connected',
    icon: '<path d="M4 4h12l4 4v12H4z"/><circle cx="12" cy="13" r="3"/><path d="M12 8v2M12 16v2M7 13h2M15 13h2"/>',
  },
  {
    name: 'SharePoint',
    subtitle: 'OEM technical library',
    description:
      'Service procedures, bulletins, PM checklists, isolation standards, site protocols, incident reports. Folder import, revision-aware.',
    stats: [
      [fx('B.docs.length'), ft(' documents')],
      [ft('3 bulletins this month')],
    ],
    tone: 'good',
    status: 'Connected',
    icon: '<path d="M5 4h9l5 5v11H5z"/><path d="M14 4v5h5M8 13h8M8 17h6"/>',
  },
  {
    name: 'OEM bulletin service',
    subtitle: 'Technical and safety bulletins (PDF)',
    description:
      'The published bulletin feed: every TB/SB as issued, linked to the affected serial numbers and their next PM so acknowledgement is tracked per machine.',
    stats: [
      [fx('B.bulletins.length'), ft(' bulletins')],
      [ft('2 this week')],
      [ft('PDF')],
    ],
    tone: 'good',
    status: 'Connected',
    icon: '<path d="M5 3h14v18l-7-4-7 4z"/>',
  },
  {
    name: 'Maximo Mobile · technician app',
    subtitle: 'Field service execution — offline-capable',
    description:
      'Assignments, labour lines, parts used, checklists, photos and customer sign-off from the technician\'s device (synced at the portal when there is no network underground); timesheets, GPS and status; service reports as PDF; shift handover notes.',
    stats: [
      [fx('B.tapp_jobs.length'), ft(' jobs')],
      [fx('B.tapp_techs.length'), ft(' technicians')],
      [ft('JSON · CSV · PDF · TXT')],
    ],
    tone: 'good',
    status: 'Connected',
    icon: '<rect x="6" y="2" width="12" height="20" rx="2"/><path d="M10 18h4"/><path d="M9 8l2 2 4-4"/>',
  },
  {
    name: 'XReach',
    subtitle: 'Remote expert support (video, AR annotations, knowledge capture)',
    description:
      'A field worker on a phone, tablet or head-mounted display streams video to a remote expert; the expert freezes frames and annotates them back to the field. Every session is logged with its outcome, first-time-fix flag and travel avoided; recordings become knowledge articles.',
    stats: [
      [fx('B.xreach_sessions.length'), ft(' sessions')],
      [
        fx('Math.round(B.xreach_sessions.filter(x=>x.first_time_fix).length/B.xreach_sessions.length*100)'),
        ft('% first-time fix'),
      ],
      [ft('JSON · CSV · PDF · MD · VTT')],
    ],
    tone: 'good',
    status: 'Connected',
    icon: '<path d="M4 6h11v12H4z"/><path d="M15 10l5-3v10l-5-3"/><circle cx="9" cy="12" r="2.5"/>',
  },
  {
    name: 'Outlook',
    subtitle: 'Email',
    description:
      'Maintenance, dispatch and parts mailboxes. Threads linked to WOs and machines by reference.',
    stats: [
      [fx('B.emails.length'), ft(' messages')],
      [ft('9 threads')],
    ],
    tone: 'good',
    status: 'Connected',
    icon: '<rect x="3" y="6" width="18" height="12" rx="2"/><path d="M3 8l9 6 9-6"/>',
  },
  {
    name: 'Microsoft Teams',
    subtitle: 'Meeting recordings & channels',
    description:
      'Reliability reviews, debriefs, planning calls — auto-transcribed with speakers.',
    stats: [
      [fx('B.meetings.length'), ft(' recordings')],
      [fx('B.meetings.reduce((a,m)=>a+m.duration_min,0)'), ft(' min')],
    ],
    tone: 'good',
    status: 'Connected',
    icon: '<rect x="3" y="6" width="13" height="12" rx="2"/><path d="M16 10l5-3v10l-5-3"/>',
  },
  {
    name: 'Training video & AR headset',
    subtitle: 'Transcribed video',
    description:
      'Training modules and technician headset recordings, auto-transcribed on import so procedures spoken on the job are searchable.',
    stats: [[ft('5 transcripts')], [ft('1 headset job')]],
    tone: 'good',
    status: 'Connected',
    icon: '<path d="M4 5h16v14H4z"/><path d="M10 9l5 3-5 3z"/>',
  },
  {
    name: 'Oil analysis lab portal',
    subtitle: 'Tribology results (CSV + PDF reports)',
    description:
      'Every sample: wear metals, water, viscosity, ISO code and the lab verdict as a results feed, with the lab\'s own PDF report per sample.',
    stats: [
      [fx('B.oil.length'), ft(' samples')],
      [fx("B.oil.filter(o=>o.verdict!=='Normal').length"), ft(' caution/critical')],
      [ft('CSV · PDF')],
    ],
    tone: 'good',
    status: 'Connected',
    icon: '<path d="M9 3h6v6l4 9a2 2 0 01-2 3H7a2 2 0 01-2-3l4-9z"/>',
  },
  {
    name: 'Tyre management',
    subtitle: 'Tyre inspections (Excel)',
    description:
      'Tread, pressure, damage and actions per wheel position from the tyre service contractor\'s system.',
    stats: [
      [fx('B.tyres.rows.length'), ft(' tyres')],
      [fx('B.tyres.rows.filter(r=>r[10]).length'), ft(' actions')],
      [ft('XLSX')],
    ],
    tone: 'good',
    status: 'Connected',
    icon: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4"/>',
  },
  {
    name: 'LMS',
    subtitle: 'Training & competency records',
    description:
      'Courses, completions and expiries for technicians, operators and supervisors — what each person is certified to do, and when it lapses.',
    stats: [
      [fx('B.lms.length'), ft(' records')],
      [fx("B.lms.filter(r=>r.status!=='Current').length"), ft(' expiring/expired')],
      [ft('CSV')],
    ],
    tone: 'good',
    status: 'Connected',
    icon: '<path d="M3 8l9-4 9 4-9 4z"/><path d="M7 10v5c0 1.5 2.5 3 5 3s5-1.5 5-3v-5"/>',
  },
  {
    name: 'Rostering / HR',
    subtitle: 'Technician roster & classification',
    description:
      'Swings, availability by day, base site, grade and customer preference notes from the rostering system.',
    stats: [
      [fx('B.roster.rows.length'), ft(' technicians')],
      [ft('W37 published')],
      [ft('XLSX')],
    ],
    tone: 'good',
    status: 'Connected',
    icon: '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/>',
  },
  {
    name: 'Customer satisfaction survey',
    subtitle: 'Real-time CSS per job',
    description:
      'In-app survey after every completed job: quality, responsiveness, reporting and a comment, tied to the technician and the customer contact.',
    stats: [
      [fx('B.css.rows.length'), ft(' responses')],
      [
        ft('mean '),
        fx('(B.css.rows.reduce((a,r)=>a+r[10],0)/B.css.rows.length).toFixed(1)'),
        ft('/5'),
      ],
      [ft('XLSX')],
    ],
    tone: 'good',
    status: 'Connected',
    icon: '<path d="M12 3l2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.3 6.4 20.2l1.1-6.2L3 9.6l6.2-.9z"/>',
  },
  {
    name: 'Rig control system',
    subtitle: 'Drill plans (IREDES-style)',
    description:
      'Per-hole drilling data from jumbos and longhole rigs: pressures, penetration, status.',
    stats: [[ft('1 plan')], [ft('68 holes')]],
    tone: 'warn',
    status: 'Partial',
    icon: '<path d="M12 3v18M5 8h14M7 14h10"/>',
  },
];

/** `CONNECTORS` keyed by name — the lookup `renderModule` does at demo line 3016. */
export const CONNECTOR_BY_NAME: Readonly<Record<string, OemConnector>> = Object.fromEntries(
  CONNECTORS.map((c) => [c.name, c]),
);
